import { memo } from "react";

import { Button } from "@react/components/Button";
import { FeaturedItem } from "@react/components/FeaturedItem";
import { Heading } from "@react/components/Heading";
import { Icon } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import {
  type CartCloseCartDrawer,
  type CartEmptyDrawerContent,
} from "@tablescape/state";
import { tw } from "classnames";

type Props = CartEmptyDrawerContent & {
  baseHeading: string;
  handleCloseCartDrawer: CartCloseCartDrawer;
};

const styles = {
  emptyState: tw(
    "w-full",
    "h-screen",
    "max-h-dynamic-screen",
    "flex",
    "flex-col",
    "bg-background-primary"
  ),
  header: tw(
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "pl-6",
    "pr-2",
    "flex",
    "items-center",
    "justify-between"
  ),
  heading: tw("mt-1"),
  closeButton: tw(
    "p-0",
    "bg-transparent",
    "border-0",
    "cursor-pointer",
    "w-12",
    "h-12",
    "inline-flex",
    "justify-center",
    "items-center"
  ),
  closeIcon: tw("w-6", "h-6"),
  body: tw("px-6", "py-8", "overflow-y-auto"),
  title: tw("text-primary", "mb-7.5", "whitespace-pre-wrap"),
  featuredItemsTitle: tw("mb-5", "text-primary-muted"),
  featuredItems: tw("flex", "flex-col", "gap-y-4"),
  footer: tw(
    "mt-auto",
    "border-0",
    "border-t",
    "border-solid",
    "border-divider",
    "p-4"
  ),
};

const CartEmptyViewBase = ({
  baseHeading,
  featuredItems,
  featuredItemsTitle,
  footerCta,
  handleCloseCartDrawer,
  heading,
}: Props) => {
  return (
    <div className={styles.emptyState}>
      <div className={styles.header}>
        <Heading as="h2" className={styles.heading} size="xs">
          {baseHeading}
        </Heading>
        <button
          aria-label="close cart drawer"
          className={styles.closeButton}
          onClick={handleCloseCartDrawer}
        >
          <Icon className={styles.closeIcon} name="close" />
        </button>
      </div>
      <div className={styles.body}>
        <Text as="body0" className={styles.title}>
          {heading}
        </Text>
        <Text as="title2" className={styles.featuredItemsTitle}>
          {featuredItemsTitle}
        </Text>
        <div className={styles.featuredItems}>
          {featuredItems.map((featuredItem) => (
            <FeaturedItem
              {...featuredItem}
              key={`featuredItem_${featuredItem.name}`}
            />
          ))}
        </div>
      </div>
      <div className={styles.footer}>
        {footerCta?.url && (
          <Button as="link" fluid to={footerCta.url} type="secondary">
            {footerCta.text}
          </Button>
        )}
      </div>
    </div>
  );
};

export const CartEmptyView = memo(CartEmptyViewBase);
